import { TKey } from '~/src/dash/publicaUI';

export interface ISankeyNodeData {
  name: string;
  total: number;
  percentage: number;
  values: Record<string, number>;
}

export interface IEChartsSankeyLabelParams {
  name: string; // Node name
  value: number; // Node value
  data: IEChartsSankeyNode; // Full node data object
  dataIndex: number; // Index in the data array
  dataType: 'node'; // Always 'node' for node labels
  color: string; // Node color
}

export interface IEChartsSankeyNode {
  name: string;
  itemStyle: {
    color: string;
  };
  depth?: number;
  label?: {
    show?: boolean;
    formatter?: string | ((params: IEChartsSankeyLabelParams) => string);
    position?: string;
    distance?: number;
    fontSize?: number;
    color?: string;
  };
  // Original node data from API
  nodeData?: ISankeyNodeData;
}

export interface IEChartsSankeyLink {
  source: string;
  target: string;
  value: number;
}

export interface IEChartsSankeyData {
  nodes: IEChartsSankeyNode[];
  links: IEChartsSankeyLink[];
}

export interface ISankeyNodeConfig {
  tKey: TKey;
  color: string;
  order: number;
  depth: number; // Horizontal position in Sankey (column)
  isTerminal?: boolean; // Whether the node is a terminal node
  tooltipTKey?: TKey;
}

export const getSankeyNodesToExclude = (
  nodeKey: string,
  nodesConfig: Partial<Record<string, ISankeyNodeConfig>>,
): string[] => {
  const nodesToExclude: string[] = [];
  const targetNode = nodesConfig[nodeKey];

  if (!targetNode) return nodesToExclude;

  const targetDepth = targetNode.depth;
  const isTargetTerminal = targetNode.isTerminal || false;

  // Determine the exclusion depth threshold
  // For terminal nodes: exclude left of parent (depth < targetDepth - 1)
  // For non-terminal nodes: exclude left of self (depth < targetDepth)
  const exclusionDepth = isTargetTerminal ? targetDepth - 1 : targetDepth;

  // Iterate through all nodes to find what to exclude
  Object.entries(nodesConfig).forEach(([key, config]) => {
    if (!config) return;

    const nodeDepth = config.depth;
    const isNodeTerminal = config.isTerminal || false;

    // Rule 1: Exclude all nodes to the left of the threshold
    if (nodeDepth < exclusionDepth) {
      nodesToExclude.push(key);
      return;
    }

    // Rule 2: Exclude terminal siblings at the exclusion depth
    // For non-terminal: terminal siblings at same depth
    // For terminal: terminal siblings at parent's depth (depth - 1)
    if (nodeDepth === exclusionDepth && isNodeTerminal) {
      nodesToExclude.push(key);
    }
  });

  return nodesToExclude;
};

export interface ISankeyRawData {
  nodes: Record<string, ISankeyNodeData>;
  links: IEChartsSankeyLink[];
}

export const formatSankeyData = (
  data: ISankeyRawData,
  nodesToExclude: string[],
  nodesConfig: Partial<Record<string, ISankeyNodeConfig>>,
  defaultColor: string,
): IEChartsSankeyData => {
  const sankeyLinks: IEChartsSankeyLink[] = [];
  const nodesInUse = new Set<string>();

  for (const link of data?.links || []) {
    // Skip links that involve any excluded nodes
    if (nodesToExclude.includes(link.source) || nodesToExclude.includes(link.target)) {
      continue;
    }

    if (link.value && link.value > 0) {
      sankeyLinks.push({
        source: link.source,
        target: link.target,
        value: link.value,
      });
      nodesInUse.add(link.source);
      nodesInUse.add(link.target);
    }
  }

  // Calculate minimum depth among remaining nodes for depth adjustment
  let minDepth = Infinity;
  nodesInUse.forEach((nodeId) => {
    const nodeDepth = nodesConfig[nodeId]?.depth;
    if (nodeDepth !== undefined && nodeDepth < minDepth) {
      minDepth = nodeDepth;
    }
  });
  const depthAdjustment = minDepth === Infinity ? 0 : minDepth;

  const sankeyNodes: IEChartsSankeyNode[] = Array.from(nodesInUse)
    .sort((a, b) => {
      const orderA = nodesConfig[a]?.order || 99;
      const orderB = nodesConfig[b]?.order || 99;
      return orderA - orderB;
    })
    .map((nodeId) => {
      const nodeConfig = nodesConfig[nodeId];
      let adjustedDepth = nodeConfig?.depth;

      // Adjust depth to shift leftmost node to depth 0
      if (adjustedDepth !== undefined) {
        adjustedDepth = adjustedDepth - depthAdjustment;
      }

      return {
        name: nodeId,
        value: data.nodes[nodeId]?.total || 0,
        depth: adjustedDepth,
        itemStyle: {
          color: nodeConfig?.color || defaultColor,
        },
        // Store original data for labels and tooltips
        nodeData: data.nodes[nodeId],
      };
    });

  return {
    nodes: sankeyNodes,
    links: sankeyLinks,
  };
};
